import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { generateAuditReport } from "../utils/pdfGenerator";

const AdminAuditLogs = ({ user }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLogs = useCallback(async () => {
    try {
      const res = await axios.get("/api/audit-logs", {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (res.data.success) {
        setLogs(res.data.data);
      }
      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch audit logs", err);
      setError("Failed to load audit logs");
      setLoading(false);
    }
  }, [user.token]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  if (loading) {
    return <div className="text-center py-10 font-bold text-gray-500">Loading logs...</div>;
  }

  if (error) {
    return <div className="text-center py-10 font-bold text-red-500">{error}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl md:text-2xl font-black text-gray-800">System Audit Logs</h2>
          <p className="text-sm text-gray-500 mt-1">Track staff and admin actions across the system</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => generateAuditReport(logs)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 font-bold rounded-xl text-sm hover:bg-purple-200 transition-all border border-purple-200"
          >
           PDF Report
          </button>
          <button
            onClick={fetchLogs}
            className="px-4 py-2 bg-purple-50 text-purple-600 font-bold rounded-xl shadow-sm hover:bg-purple-100 transition-colors text-sm"
          >
           Refresh
          </button>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-[#e1ebf0] shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-[#e1ebf0]">
                <th className="py-4 px-6 text-xs font-black text-gray-500 uppercase tracking-widest whitespace-nowrap">Timestamp</th>
                <th className="py-4 px-6 text-xs font-black text-gray-500 uppercase tracking-widest">User</th>
                <th className="py-4 px-6 text-xs font-black text-gray-500 uppercase tracking-widest">Action Type</th>
                <th className="py-4 px-6 text-xs font-black text-gray-500 uppercase tracking-widest w-1/2">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e1ebf0]">
              {logs.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center py-10 text-gray-400 font-bold">No logs found</td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log._id} className="hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-6 text-sm text-gray-600 font-medium whitespace-nowrap">
                      {new Date(log.createdAt).toLocaleString("en-US", {
                        month: "short", day: "numeric", year: "numeric",
                        hour: "numeric", minute: "2-digit"
                      })}
                    </td>
                    <td className="py-4 px-6">
                      <p className="text-sm font-bold text-gray-800">{log.userName}</p>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase mt-1 inline-block ${
                        log.userRole === "admin" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"
                      }`}>
                        {log.userRole}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <span className="px-3 py-1 bg-gray-100 text-gray-700 text-[11px] font-black rounded-lg uppercase">
                        {log.action.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-700 font-medium">
                      {log.details}
                      {log.target && (
                        <span className="ml-2 text-xs text-gray-400 block mt-1">Target: {log.target}</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminAuditLogs;
